import React, { useCallback, useContext, useMemo } from 'react';
import type {
  VoyageCharterUpdatePatch,
  VoyageCharterUpdates_UpdateFragment,
} from '../../graphql';
import {
  useAcceptVoyageCharterUpdateMutation,
  useAllVoyageCharterUpdatesLazyQuery,
  useDeleteVoyageCharterUpdateMutation,
  useVoyageCharterUpdatesQuery,
} from '../../graphql';
import { assertDefined, isDefined } from '../../lib/util';
import {
  Box,
  Button,
  Heading,
  Table,
  TableHeader,
  TableHeaders,
  TableCell,
  TableRow,
  Badge,
  Text,
} from '../ui';
import TimeField, { timeFieldFormat } from '../ui/TimeField';
import { toast } from 'react-toastify';
import { gql } from '@apollo/client';
import { VoyageCharterUpdateRequestForm } from './VoyageCharterUpdate.shipowner';
import { useCurrentUser } from '../../state/zustand';
import { mapGroupBy } from '@seaber/common/lib/collections';

export interface ShipmentVoyageCharterUpdatesProps {
  onApprove?: (update: VoyageCharterUpdates_UpdateFragment) => void;
  voyageCharterId: number;
}

export const ShipmentVoyageCharterUpdates = ({
  onApprove,
  voyageCharterId,
}: ShipmentVoyageCharterUpdatesProps) => {
  const currentUser = useCurrentUser();
  assertDefined(currentUser, 'user should be logged in');

  const {
    data: queryData,
    error: queryError,
    refetch: queryRefetch,
  } = useVoyageCharterUpdatesQuery({
    variables: {
      voyageCharterId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [acceptVoyageCharterUpdate, { loading: acceptLoading }] =
    useAcceptVoyageCharterUpdateMutation({
      update: (cache, { data, errors }, { variables }) => {
        if (errors || !variables) return;

        const updateId = variables.input.voyageCharterUpdateId;

        cache.evict({
          id: cache.identify({
            __typename: 'VoyageCharterUpdate',
            id: updateId,
          }),
        });

        const voyageCharter = data?.acceptVoyageCharterUpdate?.voyageCharter;

        if (!voyageCharter) return;

        cache.writeFragment({
          id: cache.identify({
            __typename: 'VoyageCharter',
            id: voyageCharter.id,
          }),
          fragment: gql`
            fragment UpdatedVoyageCharter on VoyageCharter {
              id
              layday
              cancelling
              vesselId
              shipByVesselId {
                id
                name
              }
            }
          `,
          data: voyageCharter,
          overwrite: true,
        });
      },
    });

  const [deleteVoyageCharterUpdate, { loading: deleteLoading }] =
    useDeleteVoyageCharterUpdateMutation({
      update: (cache, { errors }, { variables }) => {
        if (errors || !variables) return;

        const updateId = variables.input.id;

        cache.evict({
          id: cache.identify({
            __typename: 'VoyageCharterUpdate',
            id: updateId,
          }),
        });
      },
    });

  const pendingUpdates =
    queryData?.allVoyageCharterUpdates?.nodes
      .filter(isDefined)
      .map((update) => {
        const changes: {
          key: keyof VoyageCharterUpdatePatch;
          label: string;
          value: string;
        }[] = [];

        if (update.vesselId !== null && update.shipByVesselId != null) {
          changes.push({
            key: 'vesselId',
            label: 'Vessel',
            value: update.shipByVesselId.name ?? 'Vessel N/A',
          });
        }

        if (update.layday) {
          changes.push({
            key: 'layday',
            label: 'Layday',
            value: timeFieldFormat(update.layday),
          });
        }

        if (update.cancelling) {
          changes.push({
            key: 'cancelling',
            label: 'Cancelling',
            value: timeFieldFormat(update.cancelling),
          });
        }

        return {
          ...update,
          changes,
        };
      }) ?? [];

  const handleApprove = async (update: VoyageCharterUpdates_UpdateFragment) => {
    try {
      await acceptVoyageCharterUpdate({
        variables: {
          input: {
            voyageCharterUpdateId: update.id,
            accept: true,
          },
        },
      });
    } catch (err) {
      toast.error('Error approving voyage charter update');
      console.error('Error approving voyage charter update', err.message);
      return;
    }

    queryRefetch();

    onApprove?.(update);
  };

  const handleReject = async (updateId: number) => {
    try {
      await acceptVoyageCharterUpdate({
        variables: {
          input: {
            voyageCharterUpdateId: updateId,
            accept: false,
          },
        },
      });
    } catch (err) {
      toast.error('Error rejecting voyage charter update');
      console.error('Error rejecting voyage charter update', err.message);
    }

    queryRefetch();
  };

  const handleCancel = async (updateId: number) => {
    try {
      await deleteVoyageCharterUpdate({
        variables: { input: { id: updateId } },
      });
    } catch (err) {
      toast.error('Error cancelling voyage charter update');
      console.error('Error cancelling voyage charter update', err.message);
    }

    queryRefetch();
  };

  const onFormSave = () => {
    queryRefetch();
  };

  if (queryError) {
    return (
      <Box
        mb={4}
        bg="neutral.white_100"
        borderColor="neutral.gray_225"
        borderWidth={1}
        borderStyle="solid"
        p={4}
      >
        <Text color="status.error" fontWeight="bold">
          Error loading voyage updates
        </Text>
      </Box>
    );
  }

  const isPendingUpdatesVisible = pendingUpdates.length > 0;

  return (
    <Box mb={4}>
      <VoyageCharterUpdateRequestForm
        voyageCharterId={voyageCharterId}
        isBorderBottom={!isPendingUpdatesVisible}
        onSave={onFormSave}
      />

      {isPendingUpdatesVisible ? (
        <Box
          bg="neutral.white_100"
          borderColor="neutral.gray_225"
          borderWidth={1}
          borderStyle="solid"
          p={4}
        >
          <Heading fontWeight="bold" mb={3}>
            <Badge isRounded variant="past" mr={3}>
              {pendingUpdates.length}
            </Badge>
            Pending change requests
          </Heading>
          <Box
            bg="neutral.white_50"
            borderColor="neutral.gray_225"
            borderWidth={1}
            borderStyle="solid"
          >
            <Table>
              <TableHeaders>
                <TableHeader>Changes</TableHeader>
                <TableHeader>Requested by</TableHeader>
                <TableHeader>Date</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableHeaders>
              <tbody>
                {pendingUpdates.map((update) => (
                  <TableRow key={update.id}>
                    <TableCell>
                      {update.changes.map((change) => (
                        <span key={change.label}>
                          <strong>{change.label}:</strong> {change.value}{' '}
                        </span>
                      ))}
                    </TableCell>
                    <TableCell>
                      {update.userByCreatedByUserId?.email ??
                        'Unavailable user'}
                    </TableCell>
                    <TableCell>
                      <TimeField value={update.createdAt} />
                    </TableCell>
                    <TableCell>
                      {update.createdByUserId === currentUser.id ? (
                        <Button
                          variant="secondary"
                          onPress={() => handleCancel(update.id)}
                          disabled={deleteLoading}
                        >
                          Cancel
                        </Button>
                      ) : (
                        <Box>
                          <Button
                            variant="positive"
                            onPress={() => handleApprove(update)}
                            mr={1}
                            disabled={acceptLoading}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="secondary"
                            onPress={() => handleReject(update.id)}
                            disabled={acceptLoading}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

interface VoyageCharterUpdateContextProps {
  getVoyageCharterUpdates: () => Promise<void>;
  loading: boolean;
  updatesByVoyageCharter: Map<number, VoyageCharterUpdates_UpdateFragment[]>;
}

const VoyageCharterUpdateContext = React.createContext<
  VoyageCharterUpdateContextProps | undefined
>(undefined);

interface VoyageCharterUpdateProviderProps {
  children: React.ReactNode;
}

export const VoyageCharterUpdateProvider = ({
  children,
}: VoyageCharterUpdateProviderProps): JSX.Element => {
  const [fetchUpdates, { data, loading }] =
    useAllVoyageCharterUpdatesLazyQuery();

  const getVoyageCharterUpdates = useCallback(async () => {
    await fetchUpdates();
  }, [fetchUpdates]);

  const updatesByVoyageCharter = useMemo<
    Map<number, VoyageCharterUpdates_UpdateFragment[]>
  >(() => {
    if (!data?.allVoyageCharterUpdates) {
      return new Map();
    }

    const updates = data.allVoyageCharterUpdates.nodes.filter(isDefined);

    return mapGroupBy(updates, (update) => update.voyageCharterId);
  }, [data?.allVoyageCharterUpdates]);

  const value = useMemo(
    () => ({
      getVoyageCharterUpdates,
      loading,
      updatesByVoyageCharter,
    }),
    [getVoyageCharterUpdates, loading, updatesByVoyageCharter],
  );

  return (
    <VoyageCharterUpdateContext.Provider value={value}>
      {children}
    </VoyageCharterUpdateContext.Provider>
  );
};

export const useVoyageCharterUpdates = () => {
  const updateContext = useContext(VoyageCharterUpdateContext);

  if (!updateContext) {
    throw new Error(
      'useVoyageCharterUpdates must be used within VoyageCharterUpdateProvider',
    );
  }

  return updateContext;
};
